import {
  EMPTY_INVENTORY_FILTERS,
  PRODUCT_STATUS_LABELS,
  type InventorySearchInput,
} from '@amzjp/shared';

/**
 * §15.3.4 — "Filtre + sıralama URL'e yazılır (paylaşılabilir/geri tuşu çalışır)."
 *
 * Filtre nesnesi uzun olduğu için querystring'e tek tek yazmak yerine sıkıştırılmış
 * tek parametre kullanıyoruz. Router'a bağlanmıyoruz: `pushState`/`popstate` ile
 * tarayıcının kendi geçmişi çalışır.
 */

const PARAM = 'f';
/** Bildirim bağlantılarının parametresi: `?durum=INACTIVE` ya da `?durum=SOURCE_UNREACHABLE`. */
const DURUM_PARAM = 'durum';

function toBase64Url(value: string): string {
  return btoa(unescape(encodeURIComponent(value)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function fromBase64Url(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(escape(atob(padded)));
}

/** Cursor URL'e yazılmaz — paylaşılan bağlantı hep ilk sayfadan açılmalı. */
export function encodeFilters(input: InventorySearchInput): string {
  const { cursor: _cursor, ...rest } = input;
  return toBase64Url(JSON.stringify(rest));
}

export function decodeFilters(raw: string | null): InventorySearchInput | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(fromBase64Url(raw)) as InventorySearchInput;
    // Bozuk/eski bağlantılarda uygulamayı çökertmeyelim
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Bildirim bağlantısını süzgece çevirir. Değerler virgülle ayrılır: ürün durumu olanlar duruma,
 * kalanlar kapanma sebebine (`STALE_SOURCE_PROTECTION` gibi) gider. Sayfa eskiden bu parametreyi
 * okumuyordu; "1.200 ürün kapatıldı" bildirimi tüm kataloğu açıyordu.
 */
export function durumdanSuzgec(raw: string | null): InventorySearchInput | null {
  const degerler = (raw ?? '')
    .split(',')
    .map((d) => d.trim())
    .filter((d) => d !== '');
  if (degerler.length === 0) return null;

  const durumMu = (d: string) => d in PRODUCT_STATUS_LABELS;
  const durumlar = degerler.filter((d) => durumMu(d) && d !== 'DELETED');
  const sebepler = degerler.filter((d) => !durumMu(d));
  if (durumlar.length === 0 && sebepler.length === 0) return null;

  return {
    ...EMPTY_INVENTORY_FILTERS,
    withCount: true,
    general: {
      ...(durumlar.length > 0 ? { status: durumlar } : {}),
      ...(sebepler.length > 0 ? { statusReasons: sebepler } : {}),
    } as never,
  };
}

export function readFiltersFromUrl(): InventorySearchInput | null {
  const params = new URLSearchParams(window.location.search);
  return decodeFilters(params.get(PARAM)) ?? durumdanSuzgec(params.get(DURUM_PARAM));
}

/**
 * @param push `true` → geçmişe yeni kayıt (geri tuşu çalışsın),
 *             `false` → mevcut kaydı değiştir (sayfa değiştirme gibi ara adımlar)
 */
export function writeFiltersToUrl(input: InventorySearchInput, push = true): void {
  const params = new URLSearchParams(window.location.search);
  const encoded = encodeFilters(input);
  // Bildirimden gelinen süzgeç artık `f`'te; ikisi birden kalırsa geri tuşu şaşırır
  params.delete(DURUM_PARAM);

  // Varsayılan (boş) filtrede URL'i kirletme
  const isDefault =
    !input.source &&
    !input.general &&
    !input.jp &&
    !input.sort &&
    (input.limit ?? EMPTY_INVENTORY_FILTERS.limit) === EMPTY_INVENTORY_FILTERS.limit;
  if (isDefault) params.delete(PARAM);
  else params.set(PARAM, encoded);

  const query = params.toString();
  const url = `${window.location.pathname}${query ? `?${query}` : ''}`;

  if (push) window.history.pushState({}, '', url);
  else window.history.replaceState({}, '', url);
}
